import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Card, Form, Input, Select, Space, Typography, message } from "antd";
import type { Page } from "@zhihaoscm/entity";
import type { PortWharfBerthPageParams, PortWharfBerthVo } from "@/entity/logistics/portWharfBerth";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Button, MessageBox, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { SORT } from "@/meta/common";
import {
    HANDLING_ATTRIBUTE,
    PORT_BERTH_TYPE,
    PORT_WHARF_BERTH_GOODS_TYPE,
    PORT_WHARF_BERTH_TYPE
} from "@zhihaoscm/meta";
import { useUserStore } from "@/stores/react/authStore";
import { BerthLocationModal } from "./BerthLocationModal";

export interface BerthManagementQuery extends Omit<PortWharfBerthPageParams, "type"> {
    page: number;
    size: number;
    type: number;
    portBerthType: number[];
    handlingAttribute: number[];
    goodsType: number[];
}

export interface BerthManagementPageProps {
    loadBerths?: (query: BerthManagementQuery) => Promise<Page<PortWharfBerthVo>>;
    deleteBerth?: (id: number, type: number) => Promise<unknown>;
    permissions?: string[];
}

const defaultQuery: BerthManagementQuery = {
    page: 1,
    size: 10,
    type: PORT_WHARF_BERTH_TYPE.BERTH.code,
    portBerthType: PORT_BERTH_TYPE.options.map(option => Number(option.value)),
    handlingAttribute: HANDLING_ATTRIBUTE.options.map(option => Number(option.value)),
    goodsType: PORT_WHARF_BERTH_GOODS_TYPE.options.map(option => Number(option.value))
};

// 沿用旧 Vue 泊位删除弹窗的按钮文案，确认完成后才允许进入删除接口。
const berthDeleteConfirmOptions = {
    confirmButtonText: "确认",
    cancelButtonText: "取消"
};

/** 复用 Vue 泊位管理列表的 `/port/paging` 接口和查询参数。 */
export async function loadBerthManagement(query: BerthManagementQuery): Promise<Page<PortWharfBerthVo>> {
    const response = await shippingRequestHttp.service.get<Page<PortWharfBerthVo>>("/port/paging", { params: query });
    return response.data;
}

/** 复用 Vue 泊位删除接口，沿用 type=3 的泊位数据契约。 */
export async function deleteBerthManagement(id: number, type: number) {
    const response = await shippingRequestHttp.service.delete(`/port/${id}`, { params: { id, type } });
    return response.data;
}

const display = (value: unknown) => (typeof value === "string" || typeof value === "number" ? value : DEFAULT_VALUE);

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

const formatContact = (row: PortWharfBerthVo) => {
    const name = row.contactName?.trim();
    const phone = row.phone?.trim();
    if (!name && !phone) return DEFAULT_VALUE;
    return [name, phone].filter(Boolean).join(" ") || DEFAULT_VALUE;
};

/** 物流管理-泊位管理列表，保持 Vue 的筛选字段、枚举映射、排序和分页契约。 */
export function BerthManagementPage({
    loadBerths = loadBerthManagement,
    deleteBerth = deleteBerthManagement,
    permissions
}: BerthManagementPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const canRead = currentPermissions.includes("ROLE_BERTH_R");
    const canDeal = currentPermissions.includes("ROLE_BERTH_DEAL");
    const canDelete = currentPermissions.includes("ROLE_BERTH_W");
    const [rows, setRows] = useState<PortWharfBerthVo[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<BerthManagementQuery>(defaultQuery);
    const [keyword, setKeyword] = useState("");
    const [portName, setPortName] = useState("");
    const [harborName, setHarborName] = useState("");
    const [portBerthType, setPortBerthType] = useState(defaultQuery.portBerthType);
    const [handlingAttribute, setHandlingAttribute] = useState(defaultQuery.handlingAttribute);
    const [goodsType, setGoodsType] = useState(defaultQuery.goodsType);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadBerths(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("泊位列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadBerths, query]);

    const submitFilters = () => {
        const nextQuery: BerthManagementQuery = {
            ...defaultQuery,
            page: 1,
            portBerthType,
            handlingAttribute,
            goodsType
        };
        if (keyword.trim()) nextQuery.keyword = keyword.trim();
        if (portName.trim()) nextQuery.portName = portName.trim();
        if (harborName.trim()) nextQuery.harborName = harborName.trim();
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setKeyword("");
        setPortName("");
        setHarborName("");
        setPortBerthType(defaultQuery.portBerthType);
        setHandlingAttribute(defaultQuery.handlingAttribute);
        setGoodsType(defaultQuery.goodsType);
        setQuery(defaultQuery);
    };

    /** 删除成功后刷新当前筛选页，保持旧 Vue 列表的操作反馈。 */
    const handleDelete = useCallback(
        async (id: number) => {
            try {
                // 旧 Vue 在 ElMessageBox.confirm 的 then 回调中删除，取消/关闭时不触发写接口。
                await MessageBox.confirm("是否删除泊位信息?", "提示", berthDeleteConfirmOptions);
            } catch {
                return;
            }

            try {
                await deleteBerth(id, PORT_WHARF_BERTH_TYPE.BERTH.code);
                message.success("已删除");
                const result = await loadBerths(query);
                setRows(result.list || []);
                setTotal(result.total || 0);
            } catch {
                setErrorMessage("泊位删除失败");
            }
        },
        [deleteBerth, loadBerths, query]
    );

    const berthColumns = useMemo<TableColumnProps<PortWharfBerthVo>[]>(
        () => [
            {
                key: "index",
                label: "编号",
                width: 80,
                prop: "id",
                render: ({ row, rowIndex }) => (
                    <Link to={`/logistics/portWharfBerth/berthDetail?id=${row.id}`}>
                        {(query.page - 1) * query.size + rowIndex + 1}
                    </Link>
                )
            },
            { key: "name", label: "名称", prop: "name", render: ({ cellValue }) => display(cellValue) },
            { key: "shortName", label: "简称", prop: "shortName", render: ({ cellValue }) => display(cellValue) },
            {
                key: "portShortName",
                label: "所属码头",
                prop: "portShortName",
                render: ({ cellValue }) => display(cellValue)
            },
            {
                key: "harborShortName",
                label: "所属港口",
                prop: "harborShortName",
                render: ({ cellValue }) => display(cellValue)
            },
            { key: "fullAddress", label: "位置", prop: "fullAddress", render: ({ cellValue }) => display(cellValue) },
            {
                key: "portBerthType",
                label: "泊位类型",
                prop: "portBerthType",
                render: ({ row }) => PORT_BERTH_TYPE.getIntroByCode(row.portBerthType) || DEFAULT_VALUE
            },
            {
                key: "handlingAttribute",
                label: "装卸属性",
                prop: "handlingAttribute",
                render: ({ row }) => HANDLING_ATTRIBUTE.getIntroByCode(row.handlingAttribute) || DEFAULT_VALUE
            },
            {
                key: "goodsType",
                label: "货物类型",
                prop: "goodsType",
                render: ({ row }) => PORT_WHARF_BERTH_GOODS_TYPE.getIntroByCode(row.goodsType) || DEFAULT_VALUE
            },
            { key: "contact", label: "联系人信息", prop: "contactName", render: ({ row }) => formatContact(row) },
            {
                key: "updatedTime",
                label: "更新日期",
                prop: "updatedTime",
                sortable: true,
                render: ({ cellValue }) => formatDate(typeof cellValue === "string" ? cellValue : undefined)
            },
            {
                key: "operation",
                label: "操作",
                minWidth: 213,
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        {/* ROLE_BUTTON_BAN 沿用旧 Vue v-auth：保留入口，但禁止触发写操作。 */}
                        {canDeal ? (
                            banButton ? (
                                <Button disabled>修改</Button>
                            ) : (
                                <Link to={`/logistics/portWharfBerth/berthEdit?id=${row.id}`}>修改</Link>
                            )
                        ) : null}
                        {canRead ? <BerthLocationModal berth={row} disabled={banButton} /> : null}
                        {canDelete ? (
                            <Button disabled={banButton} onClick={() => void handleDelete(Number(row.id))}>
                                删除
                            </Button>
                        ) : null}
                    </Space>
                )
            }
        ],
        [banButton, canDeal, canDelete, canRead, handleDelete, query.page, query.size]
    );

    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "updatedTime") return;
            setQuery(current => ({
                ...current,
                page: 1,
                sortKey: order ? "updated_time" : undefined,
                sortOrder: order ? SORT.getIntroByCode(order) : undefined
            }));
        },
        []
    );

    return (
        <section aria-label="berth-management-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<PortWharfBerthVo>
                    rowKey="id"
                    data={rows}
                    columns={normalizeOperationColumns(berthColumns)}
                    emptyText="暂无泊位"
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4" wrap>
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    泊位管理
                                </Typography.Title>
                                {canDeal ? (
                                    banButton ? (
                                        <Button disabled>新增</Button>
                                    ) : (
                                        <Link to="/logistics/portWharfBerth/berthAdd">新增</Link>
                                    )
                                ) : null}
                            </Space>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item htmlFor="berth-keyword" label="泊位名称/简称">
                                    <Input
                                        aria-label="泊位名称/简称"
                                        id="berth-keyword"
                                        value={keyword}
                                        onChange={event => setKeyword(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item htmlFor="berth-port-name" label="所属码头">
                                    <Input
                                        aria-label="所属码头简称"
                                        id="berth-port-name"
                                        value={portName}
                                        onChange={event => setPortName(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item htmlFor="berth-harbor-name" label="所属港口">
                                    <Input
                                        aria-label="所属港口简称"
                                        id="berth-harbor-name"
                                        value={harborName}
                                        onChange={event => setHarborName(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="泊位类型">
                                    <Select
                                        mode="multiple"
                                        value={portBerthType}
                                        options={PORT_BERTH_TYPE.options}
                                        onChange={setPortBerthType}
                                    />
                                </Form.Item>
                                <Form.Item label="装卸属性">
                                    <Select
                                        mode="multiple"
                                        value={handlingAttribute}
                                        options={HANDLING_ATTRIBUTE.options}
                                        onChange={setHandlingAttribute}
                                    />
                                </Form.Item>
                                <Form.Item label="货物类型">
                                    <Select
                                        mode="multiple"
                                        value={goodsType}
                                        options={PORT_WHARF_BERTH_GOODS_TYPE.options}
                                        onChange={setGoodsType}
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="查询" htmlType="submit" type="primary">
                                        查询
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showSizeChanger={false}
                                onChange={page => setQuery(current => ({ ...current, page }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default BerthManagementPage;
